import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete } from '@mui/material';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Title } from '../../components/atoms/title';

import { useUniversities } from '../../hooks/universities';
import api from '../../services/api';

const schema = yup
  .object({
    name: yup.string().required('Nome da universidade é obrigatório'),
    abbreviation: yup.string().nullable(true),
    domain: yup
      .string()
      .nullable()
      .test('domain', 'O domínio deve ser inserido sem @ no início.', value =>
        value ? value.match(/^[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/) : true
      ),
    state_id: yup.string().required('Estado é obrigatório'),
  })
  .required();

export function Universities() {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      abbreviation: '',
      domain: '',
      name: '',
      state_id: '',
    },
  });

  const { universities, states } = useUniversities();
  const queryClient = useQueryClient();

  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const resetFields = () => {
    reset();
    setSelectedUniversity(null);
    setSelectedState(null);
  };

  const onSubmit = async data => {
    try {
      setIsLoading(true);
      const body = {
        ...data,
        state_id: selectedState.id,
      };

      if (selectedUniversity) {
        await api.put('/universities', body);
      } else {
        await api.post('/universities', body);
      }

      queryClient.invalidateQueries({ queryKey: 'universities' });
      toast('Universidade criada/alterada com sucesso!', { type: 'success' });
      resetFields();
    } catch (error) {
      toast('Erro ao criar/atualizar universidade', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Title>Universidades</Title>
      </Grid>

      <Grid xs={12} md={12} item>
        <Autocomplete
          options={universities}
          getOptionLabel={option => option.name}
          getOptionKey={option => option.id}
          onChange={(e, option, reason) => {
            if (reason === 'clear') {
              resetFields();
              return;
            }

            if (option?.name) {
              setSelectedUniversity(option);
              const universityState = states.find(state => state.id === option.state_id);
              setSelectedState(universityState);

              setValue('state_id', universityState.id);
              setValue('domain', option?.domain);
              setValue('name', option?.name);
              setValue('abbreviation', option?.abbreviation);
              setValue('id', option?.id);
            }
          }}
          renderInput={params => (
            <TextField
              fullWidth
              {...params}
              label='Universidade'
              variant='outlined'
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={12}>
        <TextField
          required
          id='name'
          name='name'
          label='Nome da universidade'
          fullWidth
          {...register('name')}
          error={!!errors.name}
          variant='outlined'
          InputLabelProps={{
            shrink: true,
          }}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.name?.message}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Autocomplete
          options={states}
          value={selectedState}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={option => option.name}
          onChange={(e, option, reason) => {
            if (reason === 'clear') {
              resetFields();
              return;
            }

            if (option?.name) {
              setSelectedState(option);
              setValue('state_id', option.id);
            } else {
              setSelectedState(null);
              setValue('state_id', '');
            }
          }}
          renderInput={params => (
            <TextField
              fullWidth
              {...params}
              label='Estado da universidade'
              error={!!errors.state}
              variant='outlined'
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.state?.message}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          id='abbreviation'
          name='abbreviation'
          label='Abreviação da universidade'
          fullWidth
          {...register('abbreviation')}
          error={!!errors.abbreviation}
          variant='outlined'
          InputLabelProps={{
            shrink: true,
          }}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.abbreviation?.message}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          id='domain'
          name='domain'
          label='Domínio da universidade'
          fullWidth
          {...register('domain')}
          error={!!errors.domain}
          variant='outlined'
          InputLabelProps={{
            shrink: true,
          }}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.domain?.message}
        </Typography>
      </Grid>

      <Box m='auto' mt={3}>
        <Button onClick={handleSubmit(onSubmit)} disabled={isLoading} color='primary' type='submit' variant='contained'>
          {selectedUniversity ? `Atualiza${isLoading ? 'ndo...' : 'r'}` : `Cria${isLoading ? 'ndo...' : 'r'}`}
        </Button>
      </Box>
    </Grid>
  );
}
